/**
 * Functions の画面（fn.*）と X の共有の画面（share.*・error.share_*）の文言。
 * 2026-09-28 に Money の辞書（en.money.ts）から移した（Money の了承・お金の画面ではないため Creator の持ち物）。
 * 言葉は founder の方針（MASTER-PLAN §0）に合わせて「プロジェクト / アプリ」「利用者」。
 * en.ts が `...enFnShare` で取り込む（ja.fnShare.ts が同じキーを全部持つことを型で強制）。
 */
export const enFnShare = {
  'fn.title': 'Functions',
  'fn.lead': 'Run your own server code with its own database next to your app. Billed at cost from your prepaid balance.',
  'fn.state.on': 'On',
  'fn.state.off': 'Off',
  'fn.state.paused': 'Paused',
  'fn.state.unavailable': 'Not available here',
  'fn.unavailableTitle': "This {product} server can't run Functions.",
  'fn.unavailableBody': 'Local servers usually have no Functions runtime. Everything else works as usual.',
  'fn.offBody': 'Functions are off for {env}. Turn them on here or with {cmd}.',
  'fn.pausedTitle': 'Paused: your prepaid balance ran out.',
  'fn.pausedBody': 'Anything that uses your server stops working until you top up. Your code and data are kept.',
  'fn.turnOn': 'Turn on',
  'fn.turnOff': 'Turn off',
  'fn.turnOffTitle': 'Turn off Functions for {env}?',
  'fn.turnOffBody': 'Anything that uses your server stops working (its URL answers "not found"). Your code, databases and files are kept.',
  'fn.turnedOn': 'Functions are on.',
  'fn.turnedOff': 'Functions are off.',
  'fn.url': 'URL',
  'fn.noUrl': 'No public URL on this server.',
  'fn.live': 'Live version',
  'fn.deployedAgo': 'updated {ago}',
  'fn.notDeployed': 'Not published yet. Ask your AI to "publish the server", or run {cmd}.',
  'fn.limits': 'Limits per call',
  'fn.cpu': 'Computing time (ms)',
  'fn.subRequests': 'Calls to other services',
  'fn.limitRange': '({min}–{max})',
  'fn.limitsHint': 'Stops a runaway call early, so one bug cannot run up your bill.',
  'fn.limitsBalance': 'Right now each call gets up to {cap} ms of computing time, because this limit grows with your prepaid balance. To get the full {want} ms you chose, keep at least {balance} in your balance. It goes up by itself about a minute after you add funds.',
  'fn.limitsBalanceLink': 'Add funds',
  'fn.saveLimits': 'Save limits',
  'fn.limitsSaved': 'Limits saved.',
  'fn.data': 'Databases and storage',
  'fn.noData': 'Nothing connected yet. Ask your AI to "let the server save data" and it shows up here.',
  'fn.col.binding': 'Name',
  'fn.col.kind': 'Type',
  'fn.col.created': 'Created',
  'fn.kind.d1': 'Database',
  'fn.kind.kv': 'Key-value store',
  'fn.kind.r2': 'File storage',
  'fn.kind.queue': 'Queue',
  'fn.crons': 'Schedules',
  'fn.none': 'None',
  'fn.deploys': 'Update history',
  'fn.noDeploys': 'No updates yet.',
  'fn.col.version': 'Version',
  'fn.col.message': 'Message',
  'fn.col.size': 'Size',
  'fn.col.when': 'When',

  'share.title': 'X sharing',
  'share.lead': 'Which shared links bring people in: visits, new users, plays and sales per link.',
  // 課金（payments）を見せない間の同じ文（売上の欄を出さない・2026-09-29 creator/launch-money）
  'share.leadNoSales': 'Which shared links bring people in: visits, new users and plays per link.',
  'share.range': 'Last 30 days · UTC',
  'share.visits': 'Visits',
  'share.players': 'New users',
  'share.plays': 'Plays',
  'share.sales': 'Sales',
  'share.trackingOffTitle': 'Link tracking is off for this project.',
  'share.trackingOffBody': 'Set {key} to true in your config to count visits and new users per link.',
  'share.col.link': 'Link',
  'share.col.visits': 'Visits',
  'share.col.players': 'New users',
  'share.col.plays': 'Plays',
  'share.col.sales': 'Sales',
  'share.kind.campaign': 'your link',
  'share.kind.plain': 'user share',
  'share.kind.score': 'score share',
  'share.kind.rank': 'rank share',
  'share.kind.challenge': 'challenge',
  'share.noLabel': 'Untitled',
  'share.emptyTitle': 'No shared links yet',
  'share.emptyBody': 'When users share from your app with {call}, or you make a campaign link below, results show up here.',
  'share.newLink': 'Campaign link',
  'share.newLinkLead': 'Make a link for your own post, then compare how each post did.',
  'share.label': 'Label',
  'share.labelPlaceholder': 'e.g. Launch post',
  'share.create': 'Create link',
  'share.created': 'Link created. Paste it in your post:',
  'share.createdQuery': 'Link created. Add {query} to your app URL in your post.',
  'share.linksOff': 'Campaign links are off for this project. Set {key} to true in your config.',

  // X のカードのタグの欄（2026-09-30 console/share-tags・founder 案 D: 作者が自分のソースの <head> に貼る。components/ShareTagsCard.tsx）。
  // 何が起きるかだけを書く（キャッチコピーなし・MASTER-PLAN §1c）。{cmd} = shareTagsCommand(CLI_NAME)・{env} = env.inline.*・{feature} = feature.shareImages
  'share.tags.title': 'X card tags',
  'share.tags.lead': 'Paste these tags into the <head> of every page of your app. When a URL is posted on X, this card shows up.',
  // 2026-10-05 creator/share-card: 画像の URL はどちらの環境でも公開中のアプリのもの（server share/tags.ts）= 同じタグが両方で使える。{env} は使わなくなった（渡されても無視）
  'share.tags.env': 'The image URL is your published app’s, so these same tags work in your test app and your published app: write them once. X shows the card for your published app’s URL.',
  'share.tags.label': 'tags for <head>',
  'share.tags.image': 'The card X shows',
  'share.tags.askLead': 'Or copy this and paste it to your AI agent:',
  'share.tags.askLabel': 'ask your AI agent',
  'share.tags.ask': "Add the {product} X card tags to the <head> of every page in this app. Get them with `{cmd}`, and don't change the image URLs.",
  'share.tags.imagesOff': 'Turn on {feature} and the tags show up here.',
  'share.tags.imagesOffWhere': 'Turn it on in your local file and push, or in {link}.',

  'error.share_links_disabled': 'Campaign links are off for this project.',
  'error.share_tracking_disabled': 'Link tracking is off for this project.',
} satisfies Record<string, string>;
